<template>
    <div class="rating-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('message.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('message.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('message.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <TableView v-if="tableMode" :cols="getCols()" :rows="getRows()" />
        <LineChart
            :title="$t('report.trainingStatusTimeseries')"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-else
        />
        <div class="stats">
            <StatCard :title="$t('report.totalMatchCount')" :value="stats.TotalMatchedCount" :unit="$t('wp.message')" />
            <StatCard
                :title="$t('report.totalUnmatchCount')"
                :value="stats.TotalUnMatchedCount"
                :unit="$t('wp.message')"
            />
            <StatCard :title="$t('report.matchRatio')" :value="stats.MatchRatio" />
            <StatCard :title="$t('report.unmatchRatio')" :value="stats.UnMatchRatio" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import LineChart from "./LineChart.vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import StatCard from "./StatCard.vue";
import IconButton from "../../../../Settings/components/IconButton.vue";
import TableView from "./TableView.vue";
import { expertToExcel } from "../../../../../../utils/utils";

export default Vue.extend({
    name: "TrainingChart",
    components: { IconButton, StatCard, LineChart, TableView },
    props: {
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            colors: ["#80FFA5", "#FF0087"],
            legends: [this.$t("report.matched"), this.$t("chatbotv2.unmatched")],
            stats: this.getStats(),
            tableMode: false,
        };
    },
    methods: {
        getStats() {
            const { buckets } = this.data;
            const items = _.flatMap(buckets, (bucket) => bucket.items);
            const TotalMatchedCount = _.sumBy(items, "successMatchCount");
            const TotalUnMatchedCount = _.sumBy(items, "failedMatchCount");
            const Total = TotalMatchedCount + TotalUnMatchedCount;
            const MatchRatio = Total === 0 ? 0 : (TotalMatchedCount / Total) * 100;
            const UnMatchRatio = Total === 0 ? 0 : (TotalUnMatchedCount / Total) * 100;

            return {
                TotalMatchedCount,
                TotalUnMatchedCount,
                MatchRatio: MatchRatio.toFixed(2) + "%",
                UnMatchRatio: UnMatchRatio.toFixed(2) + "%",
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.data;

            return buckets.map((item) => {
                if (timeSeriesType === "daily") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hourly") {
                    return moment(item.date).format("HH:mm");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;

            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => {
                    const value = _.chain(bucket.items)
                        .map((x) => (legend === "Matched" ? x.successMatchCount : x.failedMatchCount))
                        .sum()
                        .value();
                    return value || 0;
                });
                const seriesData = {
                    name: legend,
                    type: "line",

                    smooth: false,
                    step: false,
                    smoothMonotone: null,
                    symbol: "emptyCircle",
                    symbolSize: 4,
                    symbolRotate: null,
                    showAllSymbol: "auto",
                    connectNulls: false,
                    sampling: "none",
                    animationEasing: "linear",
                    progressive: 0,
                    hoverLayerThreshold: null,
                    lineStyle: {
                        width: 2,
                        type: "solid",
                    },
                    showSymbol: false,
                    label: {
                        position: "top",
                    },
                    emphasis: {
                        scale: true,
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        getCols() {
            return [
                {
                    field: "date",
                    label: "Date",
                    width: "20%",
                    sortable: true,
                    numeric: false,
                    type: "date",
                    format: "DD/MM/YYYY",
                },
                {
                    field: "successMatchCount",
                    label: "Matched",
                    width: "20%",
                    sortable: true,
                    numeric: true,
                    type: "number",
                },
                {
                    field: "failedMatchCount",
                    label: "Unmatched",
                    width: "20%",
                    sortable: true,
                    numeric: true,
                    type: "number",
                },
                {
                    field: "total",
                    label: "Total",
                    width: "20%",
                    sortable: true,
                    numeric: true,
                    type: "number",
                },
                {
                    field: "matchRatio",
                    label: "Match Ratio",
                    width: "20%",
                    sortable: true,
                    numeric: true,
                    type: "number",
                    format: "0.00",
                },
            ];
        },
        getRows() {
            const { buckets } = this.data;
            const rows = buckets.map((bucket) => {
                const successMatchCount = _.sumBy(bucket.items, "successMatchCount");
                const failedMatchCount = _.sumBy(bucket.items, "failedMatchCount");
                const total = successMatchCount + failedMatchCount;
                const matchRatio = total === 0 ? 0 : (successMatchCount / total) * 100;
                return {
                    date: moment(bucket.date).format("DD/MM/YYYY"),
                    successMatchCount,
                    failedMatchCount,
                    total,
                    matchRatio,
                };
            });
            return rows;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;

            const data = buckets.map((bucket) => {
                const successMatchCount = _.sumBy(bucket.items, "successMatchCount");
                const failedMatchCount = _.sumBy(bucket.items, "failedMatchCount");
                const total = successMatchCount + failedMatchCount;
                const matchRatio = total === 0 ? 0 : (successMatchCount / total) * 100;
                return {
                    date: moment(bucket.date).format("DD/MM/YYYY"),
                    Matched: successMatchCount,
                    Unmatched: failedMatchCount,
                    Total: total,
                    "Match Ratio": matchRatio.toFixed(2) + "%",
                };
            });

            expertToExcel(data, `training_status_timeseries.xlsx`, "Sheet 1");
        },
    },
    watch: {
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.rating-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1 1 25%;
            max-height: 115px;
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }
    }
}
</style>
